import { useEffect, useState } from 'react'
import { Dialog } from '@/components/ui/Dialog'
import { fetchDocumentBytes } from '@/lib/signedPreview'
import { closeXlsxPreview, useXlsxPreviewDoc } from '@/lib/xlsxPreview'

type Sheet = { name: string; html: string }

// In-app spreadsheet viewer. Mounted ONCE (OperationsLayout); shows whenever
// `openXlsxPreview` is called. Renders every sheet as an HTML table from the
// ORIGINAL xlsx — lossless, all data, nothing truncated. SheetJS is lazy-loaded
// (~1MB) so it never bloats the main bundle, and parsing happens in the browser
// (the bytes never leave the client — no third-party viewer).
export function XlsxPreviewModal() {
  const doc = useXlsxPreviewDoc()
  const [sheets, setSheets] = useState<Sheet[] | null>(null)
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null)
  const [active, setActive] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!doc) {
      setSheets(null)
      setBytes(null)
      setError(null)
      setActive(0)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    setSheets(null)
    setBytes(null)
    setActive(0)
    void (async () => {
      try {
        const buf = await fetchDocumentBytes(doc.id)
        if (!buf) throw new Error('Não foi possível obter o ficheiro.')
        if (!cancelled) setBytes(buf)
        const XLSX = await import('xlsx')
        const wb = XLSX.read(buf, { type: 'array', cellDates: true })
        const parsed = wb.SheetNames.map((name) => ({
          name,
          html: XLSX.utils.sheet_to_html(wb.Sheets[name]!),
        }))
        if (!cancelled) setSheets(parsed.length > 0 ? parsed : [{ name: '—', html: '' }])
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [doc])

  // Reuse the already-fetched bytes (no extra request) to download the ORIGINAL
  // file with its real name — a cross-origin `download` attr on the signed R2
  // URL would be ignored, but a same-origin blob URL forces the download.
  function download() {
    if (!bytes || !doc) return
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' }))
    const a = document.createElement('a')
    a.href = url
    a.download = doc.filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <Dialog
      open={!!doc}
      onClose={closeXlsxPreview}
      title={
        <div className="flex items-center justify-between gap-3">
          <span className="min-w-0 truncate">{doc?.filename ?? ''}</span>
          {bytes && (
            <button
              type="button"
              onClick={download}
              className="flex flex-shrink-0 items-center gap-1.5 rounded-md bg-brand-50 px-2.5 py-1 text-[12px] font-medium text-brand-800 hover:bg-brand-100"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Descarregar
            </button>
          )}
        </div>
      }
      widthClassName="max-w-6xl"
    >
      {loading && <p className="py-8 text-center text-[13px] text-gray-500">A carregar…</p>}
      {error && <p className="py-8 text-center text-[13px] text-rose-600">{error}</p>}
      {sheets && (
        <div className="flex flex-col gap-3">
          {sheets.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              {sheets.map((s, i) => (
                <button
                  key={s.name + i}
                  type="button"
                  onClick={() => setActive(i)}
                  className={
                    'rounded-md px-2.5 py-1 text-[12px] font-medium ' +
                    (i === active
                      ? 'bg-brand-100 text-brand-800'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                  }
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          <div
            className="xlsx-preview max-h-[70vh] overflow-auto rounded-md border border-DEFAULT"
            // SheetJS escapes cell content into HTML entities, so the rendered
            // table carries no executable markup from the spreadsheet data.
            dangerouslySetInnerHTML={{ __html: sheets[active]?.html ?? '' }}
          />
        </div>
      )}
    </Dialog>
  )
}
